import { useApiActionToast } from '@globals/toaster/useToasterStore'
import { isRewardEmailRequiredError } from '@kudos/http-client'
import { isAxiosError } from 'axios'
import { FieldValues, UseFormSetError } from 'react-hook-form'
import { AllowlistEntry, setErrorsFromApiError } from './setErrorsFromApiError'
import { ApiError, ApiErrorResponseData } from './types'

/**
 * Hook that returns `handleApiError` for mutations and form submits: either surface the server message on the
 * form (via `setError`) or fall back to a failure toast.
 *
 * **Behavior**
 *
 * - **Axios with `response.data`** — Pass `setError` and `fields` to forward validation payloads to
 *   {@link setErrorsFromApiError} (field and `root` errors on the form). If the response includes field paths in
 *   `details` and both arguments are present, the failure toast is skipped so users rely on the form. A toast
 *   with `msg` runs when there are no field paths, or when `setError` or `fields` is omitted.
 * - **Otherwise** — Shows `error.message` in a toast (non-Axios or missing body).
 *
 * Use the same generic `T` as your `useForm<T>()` so `fields` lines up with `Path<T>`. For allowlist rules
 * (prefix matching, wildcard `.*` suffix, `[]`, etc.), see {@link setErrorsFromApiError}.
 *
 * @template T Form values type; should match `useForm<T>`.
 *
 * @returns `{ handleApiError }` — `handleApiError(error, setError?, fields?)` where `error` is the caught
 *   value, optional `setError` / `fields` enable {@link setErrorsFromApiError} (`fields` is the allowlist;
 *   wildcard suffix `.*` opts entries into deep API paths).
 *
 * @example
 * ```ts
 * const { handleApiError } = useApiError<MyForm>()
 * // Form + allowlist: map validation to fields, toast only when there are no field paths or args missing
 * onError: err => handleApiError(err, methods.setError, ['email', 'name'])
 *
 * // useFieldArray-style nested errors: wildcard entry keeps per-item paths.
 * onError: err => handleApiError(err, methods.setError, ['config.milestones.*'])
 *
 * // Toast only (e.g. CSV export)
 * onError: err => handleApiError(err)
 * ```
 */
export const useApiError = <T extends FieldValues>() => {
  const [_, showFailure] = useApiActionToast()

  const handleApiError = (
    error: ApiError,
    setError?: UseFormSetError<T>,
    fields?: AllowlistEntry<T>[],
  ) => {
    // `RewardEmailRequired` is handled globally by the interceptor in AwaitAuth,
    // which redirects the (deskless) member to the email-capture screen. Showing
    // a failure toast / form error here too would double up the UX on the same
    // API failure, so swallow it and let the redirect take over. See epic SYN-7280.
    if (isRewardEmailRequiredError(error)) return

    if (isAxiosError(error) && error.response?.data) {
      const { msg, details } = error.response.data as ApiErrorResponseData

      const hasFieldErrors = details?.some(detail => detail.path?.length) || false

      if (setError && fields) {
        setErrorsFromApiError(error, setError, fields)
      }

      if (!hasFieldErrors || !setError || !fields) {
        showFailure(msg)
      }
    } else {
      showFailure(error.message)
    }
  }

  return { handleApiError }
}
